import { Box, Button, Icon, Modal, Spinner, useOpenClose } from '@palmetto/palmetto-components';

import { Flag, FlagStatus } from 'types';

import { ProgressBar } from './ProgressBar';

type TasksFooterProps = {
  displayIndex: number;
  totalNumberOfFlags: number;
  categoryLabel: string;
  problemsSummary: string;
  finalProblemSummary: string;
  flag: Flag;
  changesOnFlag: boolean;
  savingFlagLoading: boolean;
  from?: string;
  onSkipClick: (navigateBack?: boolean) => void;
  totalNumberOfFlagsWithoutActionTaken: number;
  handleSave: () => Promise<void>;
  isReviewer: boolean;
  onDoneClick?: () => void;
};
export const TasksFooter = ({
  displayIndex,
  totalNumberOfFlags,
  categoryLabel,
  problemsSummary,
  finalProblemSummary,
  flag,
  savingFlagLoading,
  changesOnFlag,
  totalNumberOfFlagsWithoutActionTaken,
  onSkipClick,
  handleSave,
  onDoneClick,
  isReviewer,
}: TasksFooterProps) => {
  const { isOpen, handleClose, handleOpen } = useOpenClose();
  const flagIsOpen = flag?.status === FlagStatus.open;
  const allFlagsActioned = totalNumberOfFlagsWithoutActionTaken === 0;

  const handleSkip = ({ direction }: { direction: 'left' | 'right' }) => {
    const navigateBack = direction === 'left';

    if (changesOnFlag) {
      handleOpen();
    } else {
      onSkipClick(navigateBack);
    }
  };

  return (
    <Box
      direction="column"
      position="fixed"
      style={{
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {flagIsOpen && <ProgressBar completed={displayIndex} total={totalNumberOfFlags} />}
      <Box
        direction="row"
        background="black"
        color="white"
        padding="md 5xl"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box childGap="xs">
          <Box direction="row" alignItems="center" fontSize="md" padding="xs 0 0 0">
            <Icon name={flagIsOpen ? 'radio-btn-unchecked' : 'radio-btn-checked'} size="md" margin="0 sm 0 0" />
            &nbsp;
            {problemsSummary ? problemsSummary : flag?.reason} {categoryLabel} - {finalProblemSummary}
          </Box>
          {flagIsOpen && (
            <Box fontWeight="medium" fontSize="md" direction="row" alignItems="center" childGap="sm">
              <Icon
                name="caret-left"
                cursor="pointer"
                size="sm"
                margin="0 sm 0 0"
                onClick={() => handleSkip({ direction: 'left' })}
              />
              <Box>
                Flag {displayIndex} of {totalNumberOfFlags}
              </Box>
              <Icon
                name="caret-right"
                cursor="pointer"
                size="sm"
                margin="0 sm 0 0"
                onClick={() => handleSkip({ direction: 'right' })}
              />
            </Box>
          )}
        </Box>
        <Box display="flex" direction="column" alignItems="flex-end" childGap="xs">
          {totalNumberOfFlagsWithoutActionTaken > 0 && (
            <Box fontSize="sm">
              {totalNumberOfFlagsWithoutActionTaken} left to {isReviewer ? 'review' : 'action'}
            </Box>
          )}
          <Box display="flex" direction="row" childGap="md" alignItems="center">
            {flagIsOpen ? (
              <>
                {totalNumberOfFlags > 1 && !allFlagsActioned && (
                  <Box size="sm" padding="xs" tone="neutral" cursor="pointer" onClick={handleSkip}>
                    Skip
                  </Box>
                )}
                <Box
                  size="sm"
                  padding="xs"
                  radius="md"
                  borderWidth="md"
                  tone="neutral"
                  borderColor="grey-900"
                  cursor={changesOnFlag ? 'pointer' : 'not-allowed'}
                  background={changesOnFlag ? 'button-primary' : 'black'}
                  onClick={() => {
                    if (changesOnFlag && !savingFlagLoading) {
                      handleSave();
                    }
                  }}
                >
                  <Box width="lg" alignItems="center">
                    {savingFlagLoading ? <Spinner /> : 'Next'}
                  </Box>
                </Box>
              </>
            ) : (
              <Box display="flex" direction="column" childGap="xs">
                {allFlagsActioned && <Box fontSize="sm">All flags actioned</Box>}
                <Button aria-label="done" onClick={onDoneClick}>
                  Done
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Modal isOpen={isOpen} onDismiss={handleClose}>
        <Modal.Header id="title" title={`${categoryLabel} Flag`} onDismiss={handleClose} />
        <Modal.Body background="secondary">
          <Box fontWeight="bold">Are you sure you want to Skip? Your progress on this Flag will not be saved.</Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSkipClick();
              handleClose();
            }}
          >
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};
